import axios from "axios";
import { closeAllStreams } from "./sseRegistry";
import { storedLanguage } from "../i18n";

// Gatsby's DefinePlugin inlines `process.env.GATSBY_*` literally at build time —
// don't wrap it in a typeof guard or the substitution silently fails in the browser bundle.
const baseURL = process.env.GATSBY_API_BASE || "http://localhost:8087";

export const api = axios.create({
  baseURL,
  timeout: 15000,
  // Auth now lives in HttpOnly cookies (coride_at / coride_rt). The browser only sends/receives
  // them cross-origin when withCredentials is set AND the server allows credentials (CORS).
  withCredentials: true,
});

/*
 * Tell the server which language to write its messages in.
 *
 * The server authors most of what the user reads on a failure — see `errors.js`, which shows the
 * server's sentence verbatim whenever there is one — so a request that doesn't say what language
 * it wants gets a validation message in the wrong one, on a screen that is otherwise translated.
 *
 * Read per request rather than captured once at module load: the header has to change the moment
 * the switch is flipped, and this module is evaluated long before anyone reaches Profile. Reading
 * localStorage rather than context because an axios interceptor is not inside the React tree —
 * `LocaleContext` keeps that copy current, and `storedLanguage` lives in `i18n` precisely so this
 * import cannot close a cycle back through AuthContext.
 */
api.interceptors.request.use((config) => {
  config.headers["Accept-Language"] = storedLanguage();
  return config;
});

// Non-HttpOnly hint the server sets on login/refresh and clears on logout. We can't read the
// auth cookies (HttpOnly by design), so this readable flag tells us whether a session plausibly
// exists — letting us skip the doomed /refresh for visitors who were never logged in.
const SESSION_HINT = "coride_session";
function hasSessionHint() {
  if (typeof document === "undefined") return false;
  return document.cookie.split("; ").some((c) => c === `${SESSION_HINT}=1`);
}
function clearSessionHint() {
  // A failed /refresh means the session is truly over — drop the stale hint so later 401s don't
  // keep retrying. (The server also clears it on logout; this covers expiry/revocation.)
  if (typeof document !== "undefined") {
    document.cookie = `${SESSION_HINT}=; Path=/; Max-Age=0; SameSite=Strict`;
  }
  clearAccessDeadline();
  forgetSignedIn();
}

/*
 * "This browser was signed in the last time we heard from the server", kept by the SPA itself.
 *
 * The hint cookie above cannot carry that on its own. It is host-only on the API's host, so it is
 * readable here only while the app and the API share one (localhost in development); in production
 * the site and the API live on different hosts and `document.cookie` never sees it. This flag is
 * written on this origin — on every login, refresh and successful /me — and dropped on logout and
 * whenever the session is found to be over, so the two together answer "could there be a session?"
 * wherever the app is deployed.
 *
 * localStorage so every tab agrees. It is a yes/no, never a credential: a wrong "yes" costs one
 * /me that answers 401, a wrong "no" on a public page only shows the sign-in form to someone who
 * could have gone straight on — and inside /app the question is always asked (see sessionMayExist).
 */
const SIGNED_IN_KEY = "coride_signed_in";
function rememberSignedIn() {
  try {
    window.localStorage.setItem(SIGNED_IN_KEY, "1");
  } catch (_) { /* storage blocked: the hint cookie and /app still decide */ }
}
function forgetSignedIn() {
  try {
    window.localStorage.removeItem(SIGNED_IN_KEY);
  } catch (_) { /* nothing stored, or storage blocked */ }
}
function rememberedSignedIn() {
  try {
    return window.localStorage.getItem(SIGNED_IN_KEY) === "1";
  } catch (_) {
    return false;
  }
}

/**
 * Whether asking the server "who am I?" can possibly come back with someone.
 *
 * A signed-out visitor on the sign-in page is the normal case, not a failure, yet asking /me for
 * them always produced a 401 — which the browser prints as a red console error on every public page
 * load, no script can hide it, and it buried the errors that matter. So the question is skipped when
 * nothing suggests a session: no hint cookie, no remembered sign-in, and not inside /app. Inside
 * /app it is always asked, for the reason the 401 handler below spells out — being there is reason
 * enough, and a missing hint must never throw a live session away.
 *
 * @returns {boolean} false only when the server is certain to answer 401
 */
export function sessionMayExist() {
  if (typeof window === "undefined") return false;
  return hasSessionHint() || rememberedSignedIn() || window.location.pathname.startsWith("/app");
}

/*
 * When the access cookie runs out, as a wall-clock time.
 *
 * Login and refresh both answer with `expiresInMinutes`, and the server gives the access cookie
 * exactly that Max-Age. Remembering the deadline lets a request that is about to be sent on a dead
 * cookie renew it FIRST, instead of going out, coming back 401 and being replayed. The replay
 * always worked — the user stayed signed in — but every such request was still a red 401 line in
 * the browser console (the browser logs a failed request itself; no script can hide it), and a
 * tab left idle past the 30-minute lifetime produced a burst of them on the next screen it opened.
 *
 * localStorage rather than a module variable so every open tab shares the one deadline: a refresh
 * in one tab moves it for all of them. It is a timestamp, not a credential. Anything unreadable —
 * blocked storage, a session that predates this, a garbled value — is simply "unknown", and an
 * unknown deadline falls back to the reactive 401 path below, exactly as before.
 */
const ACCESS_DEADLINE_KEY = "coride_access_until";
// Renew slightly early: the request still has to travel, and the server's clock is not ours.
const RENEW_EARLY_MS = 60 * 1000;

function readAccessDeadline() {
  try {
    const v = Number(window.localStorage.getItem(ACCESS_DEADLINE_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch (_) {
    return null;
  }
}
function recordAccessDeadline(expiresInMinutes) {
  const minutes = Number(expiresInMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return;
  try {
    window.localStorage.setItem(ACCESS_DEADLINE_KEY, String(Date.now() + minutes * 60 * 1000));
  } catch (_) { /* storage blocked: stay on the reactive path */ }
}
function clearAccessDeadline() {
  try {
    window.localStorage.removeItem(ACCESS_DEADLINE_KEY);
  } catch (_) { /* nothing stored, or storage blocked */ }
}
function accessCookieDueForRenewal() {
  if (typeof window === "undefined" || !hasSessionHint()) return false;
  const deadline = readAccessDeadline();
  return deadline !== null && Date.now() >= deadline - RENEW_EARLY_MS;
}

const isAuthUrl = (url) => typeof url === "string" && url.includes("/api/auth/");

/**
 * The session is genuinely over: /refresh was rejected, or there was nothing left to renew.
 * Drop the stale hint and, when we're inside the authenticated app, hard-redirect to /login.
 *
 * The full-document navigation is load-bearing: it unmounts everything and closes every open
 * EventSource. SSE streams are authenticated only at the handshake, so a stream that's already
 * connected keeps delivering on a dead session until the connection happens to drop — this is
 * what forces it shut. Both the axios interceptor (a normal request 401'd) and ensureFreshSession
 * (a stream tried to renew and failed) route through here, so the sign-out is identical whichever
 * one notices the session died first.
 */
function endSession() {
  clearSessionHint();
  closeAllStreams(); // cut open SSE connections now, don't wait on the navigation to unload them
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/app")) {
    window.location.href = "/login?reason=expired";
  }
}

// Single-flight refresh: many requests can 401 at once when the access cookie expires; they all
// share ONE /refresh call rather than stampeding the endpoint (and rotating the token repeatedly).
let refreshPromise = null;
function refreshOnce() {
  if (!refreshPromise) {
    refreshPromise = api
      .post("/api/auth/refresh")
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * Renew the access cookie for callers that aren't axios requests — chiefly EventSource, which
 * can't carry an interceptor and whose handshake simply fails when the cookie has expired.
 * Shares the same single-flight promise as the interceptor below, so a stream reconnect and a
 * burst of 401'd XHRs still trigger exactly one /refresh.
 *
 * Rejects when there's no session left to renew; a rejected /refresh also ends the session
 * (see {@link endSession}) so the dead stream doesn't just quietly go "offline".
 */
export async function ensureFreshSession() {
  if (!hasSessionHint()) throw new Error("no session");
  try {
    await refreshOnce();
  } catch (e) {
    // Session's gone — end it the same way a 401'd XHR would, so a failed stream renewal also
    // signs the user out and tears the (still-open) EventSources down instead of silently
    // dropping to "offline" while the app keeps believing it's logged in.
    endSession();
    throw e;
  }
}

/*
 * Renew BEFORE sending when the access cookie is known to have run out (see ACCESS_DEADLINE_KEY).
 * Shares refreshOnce, so a burst of requests from a screen opening after a long idle still makes
 * exactly one /refresh and all wait on it. Auth calls are skipped — /refresh itself comes through
 * here, and login/logout must never wait on a renewal.
 *
 * A refused renewal is not decided here. When the server said no (a 4xx), the request is marked
 * as already retried so its inevitable 401 ends the session at once rather than asking /refresh
 * a second time; a network failure leaves it unmarked, so the normal 401 path still gets to try.
 */
api.interceptors.request.use(async (config) => {
  if (!isAuthUrl(config.url) && accessCookieDueForRenewal()) {
    try {
      await refreshOnce();
    } catch (e) {
      const s = e?.response?.status;
      if (s >= 400 && s < 500) config._retry = true;
    }
  }
  return config;
});

api.interceptors.response.use(
  (r) => {
    // Keep the renewal deadline in step with every login and refresh, and forget it on logout.
    const url = r?.config?.url;
    if (isAuthUrl(url)) {
      if (url.includes("/api/auth/logout")) {
        clearAccessDeadline();
        forgetSignedIn();
      } else if (r.data && r.data.expiresInMinutes != null) {
        // Only login and refresh answer with a lifetime — the password-reset calls share the prefix.
        recordAccessDeadline(r.data.expiresInMinutes);
        rememberSignedIn();
      }
    } else if (r?.config?.method === "get" && url === "/api/users/me") {
      // A session that predates the flag (or began in another way) is remembered the first time
      // the server confirms it.
      rememberSignedIn();
    }
    return r;
  },
  async (err) => {
    const original = err.config;
    const status = err?.response?.status;
    // Never try to refresh in response to the auth endpoints themselves (avoids recursion).
    const isAuthCall = isAuthUrl(original?.url);
    // A logout that failed on the wire still ends the session on this side.
    if (isAuthCall && original.url.includes("/api/auth/logout")) {
      clearAccessDeadline();
      forgetSignedIn();
    }

    // Inside the authenticated app there is, by definition, a session worth trying to save.
    const inApp = typeof window !== "undefined" && window.location.pathname.startsWith("/app");

    if (status === 401 && original && !isAuthCall) {
      /*
       * Expired access cookie? Renew it. That is the whole point of the refresh token, and it
       * must be attempted before anyone is signed out.
       *
       * The hint cookie is an optimisation, not evidence: it exists so a visitor who has never
       * logged in doesn't pay a pointless 401 -> /refresh -> 401 round-trip on a public page. It
       * is readable, non-HttpOnly and quite capable of disappearing on its own — a shorter
       * expiry, privacy tooling clearing non-HttpOnly cookies, an errant `document.cookie` write
       * — while the HttpOnly refresh token is still perfectly valid.
       *
       * Gating the renewal on it, as this did, therefore threw away live sessions: the access
       * cookie expired, the hint happened to be missing, and the user was signed out mid-task
       * without `/refresh` ever being called. Being inside /app is reason enough to ask.
       */
      if (!original._retry && (hasSessionHint() || inApp)) {
        original._retry = true;
        try {
          await refreshOnce(); // new access cookie is set by the server on success
          return api(original); // replay the original request with the fresh cookie
        } catch (e) {
          // Refresh was tried and refused. Now the session really is over.
          endSession();
          return Promise.reject(e);
        }
      }

      /*
       * Nothing left to try: the replay already came back 401, or this is a public page with no
       * hint. The first case used to fall through to a bare rejection, which left the app sitting
       * on an authenticated screen where every request failed, with no sign-out and no way back
       * to the login page short of typing the URL.
       *
       * `endSession` self-limits its redirect to /app, so a 401 on a public page still just
       * rejects.
       */
      endSession();
    }
    return Promise.reject(err);
  }
);

export const apiBase = baseURL;
